import React from 'react';
import { Check, Clock, Users, Shield, Star, Headphones } from 'lucide-react';
import { motion, useInView } from 'framer-motion';

const ReasonCard = ({ icon: Icon, title, delay }) => {
  const cardRef = React.useRef(null);
  const isInView = useInView(cardRef, { once: true, amount: 0.5 });

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.7, 
        ease: "easeOut",
        delay: delay 
      }
    }
  };

  return (
    <motion.div
      ref={cardRef}
      variants={cardVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      className={`
        relative overflow-hidden
        bg-white rounded-xl p-5 md:p-6 shadow-md border border-gray-100
        transform transition-all duration-300 ease-out
        hover:scale-105 hover:shadow-lg
        group
        flex flex-col items-center
        text-center
      `}
    >
      {/* Icon Container */}
      <div className="relative mb-4 flex justify-center">
        <div className="w-12 h-12 rounded-full bg-amber-100 p-3 flex items-center justify-center transform transition-transform group-hover:scale-110">
          <Icon className="w-6 h-6 text-amber-600" />
        </div>
      </div>

      {/* Content */}
      <h3 className="text-lg font-bold text-gray-800 transition-colors duration-300">
        {title}
      </h3>
    </motion.div>
  );
};

const ReasonsList = ({ className = '' }) => {
  const listRef = React.useRef(null);
  const isInView = useInView(listRef, { once: true, amount: 0.1 });

  const allReasons = [
    { icon: Users, title: 'ליווי אישי צמוד' },
    { icon: Star, title: 'מקצועיות ומומחיות' },
    { icon: Shield, title: 'דיסקרטיות מלאה' },
    { icon: Clock, title: 'זמינות ומענה מהיר' },
    { icon: Check, title: 'שקיפות מלאה' },
    { icon: Headphones, title: 'יחס אישי' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <div
      ref={listRef}
      className={`max-w-4xl mx-auto px-4 py-4 md:py-12 bg-transparent ${className}`}
      dir="rtl"
    >
      <div className="text-center max-w-2xl mx-auto mb-10 md:mb-16">
        <motion.h2
          initial={{ y: 30, opacity: 0 }}
          animate={isInView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-3xl md:text-4xl font-bold mb-4 text-gray-800"
        >
          למה לבחור בנו?
        </motion.h2>
        <motion.div
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          className="w-24 h-1 bg-amber-400 mx-auto rounded-full"
        />
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto"
      >
        {allReasons.map((reason, index) => (
          <ReasonCard 
            key={index}
            icon={reason.icon}
            title={reason.title}
            delay={0.1 * index}
          />
        ))}
      </motion.div>
    </div>
  );
};

export default ReasonsList;
